'use client';

import Image from 'next/image';
import Link from 'next/link';
import classes from './HeaderMenu.module.scss';
import { IconChevronDown } from '@tabler/icons-react';
import { Button, Center, Container, Group, Menu } from '@mantine/core';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';
import { HeaderLinks } from '@/src/types/Header';
import LogoutButton from '../LogoutButton/LogoutButton';
import { LanguageSelector } from './LanguageSelector/LanguageSelector';
import { ResponsiveBurger } from './ResponsiveBurger/ResponsiveBurger';
import logo from '@/public/images/logo.webp';
import HeaderEditorModal from './HeaderEditorModal';
import { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { parseJwt } from '@/src/helpers/decode-jwt';
import MenuLink from '../MenuLink/MenuLink';
import HeaderBurger from '../headerBurger/HeaderBurger';
import { primaryColor } from '@/src/styles/colors';

export function HeaderMenu({ isDarkTheme }: { isDarkTheme?: boolean }) {
  const dictionary = useDictionary();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const token = Cookies.get('authToken');
    if (token) {
      try {
        const decoded = token ? parseJwt(token) : null;
        if (decoded.role === 'superAdmin' || decoded.role === 'admin') {
          setIsSuperAdmin(true);
        }
      } catch (err) {
        console.error('Invalid token', err);
      }
    }
  }, []);

  useEffect(() => {
    // Hysteresis to avoid toggle jitter
    const HIDE_AT = 120; // hide when beyond
    const SHOW_AT = 60;  // show again when above top
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || 0;
        setIsScrolled(prev => (prev ? y > SHOW_AT : y > HIDE_AT));
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const metaHeader = getHeaderItems(dictionary.header.metaHeader, 'sm');
  const mainHeader = getHeaderItems(dictionary.header.header);

  const languages = Object.keys(dictionary.supportedLanguage || {});
  const headerBurgerData = {
    header: dictionary.header.header,
    metaHeader: dictionary.header.metaHeader,
    backgroundUrl: dictionary.adminMenu?.backgroundUrl,
    languages,
    dashboard: dictionary.header.dashboard,
    login: dictionary.header.login,
    logout: dictionary.header.logout,
  } as any;

  return (
    <div className={`${classes.header} ${isDarkTheme ? classes.dark : classes.light} ${isScrolled ? classes.headerScrolled : ''}`}>
      <Menu>
        {metaHeader && (
          <div className={classes.surHeaderSpace}>
            <Group justify="flex-end" className={`${classes.surHeader} ${isScrolled ? classes.surHeaderHidden : ''}`} visibleFrom="md">
              <Container size="lg" className={classes.container}>
                {metaHeader}
                {isSuperAdmin && <HeaderEditorModal mainHeader={JSON.parse(JSON.stringify(dictionary.header.header))} metaHeader={JSON.parse(JSON.stringify(dictionary.header.metaHeader))} />}
              </Container>
            </Group>
          </div>
        )}
        {mainHeader && (
          <div className={`${classes.inner} ${isScrolled ? classes.surHeaderHiddenInner : ''}`}>
            <Container size="lg" className={classes.containerCenter}>
              <Group>
                <Group visibleFrom="md">
                  <MenuLink className={classes.logoWrap} href="/" aria-label="menu">
                    <img
                      src={dictionary.logoUrl}
                      alt="logo header"
                      height={45}
                      width={45}
                      className={`${classes.logo} ${isScrolled ? classes.logoSmall : ""}`}
                    />
                  </MenuLink>
                </Group>
                <Group hiddenFrom="md">
                  <HeaderBurger data={headerBurgerData} color={primaryColor} />
                </Group>


                <Group gap={5} visibleFrom="md">
                  {mainHeader}
                </Group>
              </Group>
              <Group pr='sm'>
                <Button
                  visibleFrom="md"
                  component={MenuLink}
                  href={dictionary.pages.contact.link}
                  className={classes.contact}
                >
                  {dictionary.pages.contact.label}
                </Button>
                <LogoutButton />
                <LanguageSelector />
              </Group>
            </Container>
          </div>
        )}
      </Menu>
    </div>
  );
}

const getHeaderItems = (links: HeaderLinks, size?: 'sm') =>
  links.map((link, index) => {
    const menuItems = link.links?.map((item) => (
      <Menu.Item key={item.label}>
        <MenuLink href={item.link} className={classes.link}>
          {item.label}
        </MenuLink>
      </Menu.Item>
    ));

    if (menuItems) {
      return (
        <Menu key={link.label + index} trigger="hover" transitionProps={{ exitDuration: 0 }} withinPortal>
          <Menu.Target>
            <Link
              href={link.link}
              className={classes.link}
              onClick={(event) => event.preventDefault()}
            >
              <Center>
                <span className={classes.linkLabel}>{link.label}</span>
                <IconChevronDown size={14} stroke={1.5} />
              </Center>
            </Link>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <MenuLink
        key={link.label + index}
        //@ts-ignore
        href={link.query ? { pathname: link.link, query: link.query } : link.link}
        className={`${classes.link} ${size ? classes.smallLink : ''}`}
      >
        {/* @ts-ignore */}
        {link.icon && (
          // @ts-expect-error
          <Image width="15" height="15" src={link.icon} alt={link.label} className={classes.icon} />
        )}
        {link.label}
      </MenuLink>
    );
  });